import React, { useState, useMemo, useEffect } from 'react';
import { Card, Button, Alert } from '../ui';

const MobileServiceSelection = ({ 
  services, 
  categories, 
  selectedServices, 
  onServiceSelect, 
  onNext, 
  loading,
  error,
  salonConfig
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Verificar e formatar as categorias
  const formattedCategories = useMemo(() => {
    if (!categories || !categories.length) return [];
    
    // Log para depuração dos dados recebidos
    console.log('Dados de categorias recebidos:', categories);
    
    return categories.map(category => ({
      id: category.id,
      nome: category.nome || `Categoria ${category.id}`, // Garantir que temos um nome
      ordem: typeof category.ordem === 'number' ? category.ordem : 0,
      descricao: category.descricao || ''
    })).sort((a, b) => (a.ordem || 0) - (b.ordem || 0)); // Ordenar por ordem
  }, [categories]);
  
  // Criar um mapa de IDs para categorias para acesso rápido
  const categoryMap = useMemo(() => {
    if (!formattedCategories.length) return {};
    
    return formattedCategories.reduce((map, category) => {
      map[category.id] = category;
      return map;
    }, {});
  }, [formattedCategories]);
  
  // Agrupar serviços por categoria
  const servicesByCategory = useMemo(() => {
    // Se não tiver busca, agrupar normalmente
    if (!searchQuery.trim()) {
      // Primeiro criar um objeto onde as chaves são as IDs das categorias
      const grouped = {};
      
      // Adicionar uma categoria "Sem categoria" para serviços sem categoria
      grouped['uncategorized'] = {
        id: 'uncategorized',
        nome: 'Outros Serviços',
        servicos: []
      };
      
      // Inicializar cada categoria do array com os dados formatados
      formattedCategories.forEach(category => {
        grouped[category.id] = {
          id: category.id,
          nome: category.nome,
          ordem: category.ordem,
          descricao: category.descricao,
          servicos: []
        };
      });
      
      // Adicionar cada serviço à sua categoria
      services.forEach(service => {
        const categoryId = service.categoria;
        
        // Ver se temos uma categoria válida para este serviço
        if (categoryId && grouped[categoryId]) {
          grouped[categoryId].servicos.push({
            ...service,
            categoriaNome: grouped[categoryId].nome // Adicionar nome da categoria no serviço para facilitar
          });
        } else {
          grouped['uncategorized'].servicos.push(service);
        }
      });
      
      // Converter para array e remover categorias sem serviços
      const result = Object.values(grouped)
        .filter(category => category.servicos.length > 0)
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
      
      console.log('Categorias agrupadas com serviços:', result);
      return result;
    } 
    // Se tiver busca, filtrar os serviços e não agrupar por categoria
    else {
      const query = searchQuery.toLowerCase().trim();
      const filteredServices = services.filter(service =>
        service.nome.toLowerCase().includes(query) ||
        (service.descricao && service.descricao.toLowerCase().includes(query))
      );
      
      if (filteredServices.length === 0) return [];
      
      // Adicionar o nome da categoria aos serviços filtrados
      const enhancedServices = filteredServices.map(service => {
        const categoryId = service.categoria;
        if (categoryId && categoryMap[categoryId]) {
          return {
            ...service,
            categoriaNome: categoryMap[categoryId].nome
          };
        }
        return service;
      });
      
      // Retornar um único "grupo" com os resultados da busca
      return [{
        id: 'search-results',
        nome: `Resultados para "${searchQuery}"`,
        servicos: enhancedServices
      }];
    }
  }, [services, formattedCategories, searchQuery, categoryMap]);
  
  // Calcula duração total e preço total dos serviços selecionados
  const totalDuration = selectedServices.reduce(
    (total, serviceId) => {
      const service = services.find(s => s.id === serviceId);
      return total + (service ? service.duracao || 0 : 0);
    }, 0
  );
  
  const totalPrice = selectedServices.reduce(
    (total, serviceId) => {
      const service = services.find(s => s.id === serviceId);
      return total + (service ? service.preco || 0 : 0);
    }, 0
  );

  // Formatação de preço
  const formatPrice = (price) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };

  // Formatação de duração
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };
  
  // Registrar informações de depuração
  useEffect(() => {
    console.log('Categorias formatadas:', formattedCategories);
    console.log('Mapa de categorias:', categoryMap);
    console.log('Serviços agrupados:', servicesByCategory);
  }, [formattedCategories, categoryMap, servicesByCategory]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-blue-500 border-r-transparent mb-4"></div>
          <p className="text-gray-600">Carregando serviços...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return <Alert type="error" title="Erro ao carregar serviços">{error}</Alert>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 space-y-4 mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Selecione os serviços desejados
        </h2>
        
        {/* Campo de busca */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Buscar serviços..."
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* Conteúdo agrupado por categoria - agora usa flex-grow para ocupar todo o espaço disponível */}
      <div className="flex-grow overflow-y-auto space-y-6 pb-24">
        {servicesByCategory.length === 0 ? (
          <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
            {searchQuery 
              ? `Nenhum serviço encontrado para "${searchQuery}"` 
              : "Nenhum serviço disponível"}
          </div>
        ) : (
          servicesByCategory.map(category => (
            <div key={category.id} className="space-y-2">
              {/* Cabeçalho da categoria */}
              <div className="px-4 mb-3">
                <h3 className="text-lg font-normal text-gray-900" style={salonConfig?.fontFamily ? { fontFamily: salonConfig.fontFamily } : {}}>
                  {(category.nome || "").toUpperCase()}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {category.descricao || "Selecione um serviço desta categoria"}
                </p>
              </div>
              
              {/* Serviços da categoria */}
              {category.servicos.map(service => (
                <div
                  key={service.id}
                  className={`p-4 rounded-lg border ${
                    selectedServices.includes(service.id) 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 bg-white'
                  } transition-all duration-200 shadow-sm`}
                  onClick={() => onServiceSelect(service.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-900">{service.nome}</h3>
                      {service.descricao && (
                        <p className="text-xs text-gray-500 mt-1">{service.descricao}</p>
                      )}
                      <div className="flex items-center mt-2 text-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-500">
                          {formatDuration(service.duracao || 0)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className="font-semibold text-gray-900">{formatPrice(service.preco || 0)}</span>
                      <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                        selectedServices.includes(service.id)
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'border-gray-300'
                      }`}>
                        {selectedServices.includes(service.id) ? '✓' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      
      {/* Barra inferior fixa com resumo e botão de continuar */}
      {selectedServices.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg border-t border-gray-200 z-10">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs text-gray-500">Total:</div>
              <div className="font-semibold">
                {formatPrice(totalPrice)} · {formatDuration(totalDuration)}
              </div>
              <div className="text-xs text-gray-500">
                {selectedServices.length} {selectedServices.length === 1 ? 'serviço' : 'serviços'}
              </div>
            </div>
            
            <Button onClick={onNext} variant="primary">
              Continuar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileServiceSelection; 